import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  Input,
  Button,
  Select,
  Icon,
  Spinner,
  Alert,
  Progress,
} from '@chakra-ui/react'
import { FiSearch, FiRefreshCw } from 'react-icons/fi'
import { apiConfig } from '../../config/api'
import { DataTable, Pagination } from '../../components/admin/DataTable'

interface Practitioner {
  practitioner_id: string
  email?: string
  name?: string
  step: number
  visibility_score?: number
  created_at?: string
  updated_at?: string
}

export const PractitionersPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [practitioners, setPractitioners] = useState<Practitioner[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [stepFilter, setStepFilter] = useState<string>(
    searchParams.get('step') || ''
  )
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 50

  const fetchPractitioners = async () => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      setError('Authentication required')
      setIsLoading(false)
      return
    }

    try {
      const params = new URLSearchParams()
      if (stepFilter) params.append('step', stepFilter)
      params.append('limit', pageSize.toString())
      params.append('offset', ((currentPage - 1) * pageSize).toString())

      const response = await fetch(
        `${apiConfig.baseURL}/api/v1/admin/practitioners?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error('Failed to load practitioners')
      }

      const result = await response.json()
      setPractitioners(result)
    } catch (err: any) {
      setError(err.message || 'Failed to load practitioners')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchPractitioners()
  }, [stepFilter, currentPage])

  useEffect(() => {
    const params = new URLSearchParams()
    if (stepFilter) params.set('step', stepFilter)
    setSearchParams(params, { replace: true })
  }, [stepFilter, setSearchParams])

  const filteredPractitioners = practitioners.filter((practitioner) => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      practitioner.practitioner_id.toLowerCase().includes(searchLower) ||
      practitioner.name?.toLowerCase().includes(searchLower) ||
      practitioner.email?.toLowerCase().includes(searchLower)
    )
  })

  const columns = [
    {
      key: 'practitioner_id',
      label: 'ID',
      render: (practitioner: Practitioner) => (
        <Text
          fontFamily="mono"
          fontSize="xs"
          color="blue.500"
          cursor="pointer"
          onClick={() =>
            navigate(`/admin/practitioners/${practitioner.practitioner_id}`)
          }
        >
          {practitioner.practitioner_id.substring(0, 12)}...
        </Text>
      ),
    },
    {
      key: 'name',
      label: 'Name',
      render: (practitioner: Practitioner) => (
        <VStack align="start" gap={0}>
          <Text fontWeight="medium">{practitioner.name || 'Unknown'}</Text>
          {practitioner.email && (
            <Text fontSize="xs" color="gray.500">
              {practitioner.email}
            </Text>
          )}
        </VStack>
      ),
    },
    {
      key: 'step',
      label: 'Onboarding Step',
      render: (practitioner: Practitioner) => (
        <VStack align="start" gap={1}>
          <Text fontWeight="medium">Step {practitioner.step}</Text>
          <Progress
            value={(practitioner.step / 5) * 100}
            size="sm"
            colorPalette="brand"
            w="100px"
          />
        </VStack>
      ),
    },
    {
      key: 'visibility_score',
      label: 'Visibility Score',
      render: (practitioner: Practitioner) => (
        <Text fontWeight="bold">
          {practitioner.visibility_score !== undefined
            ? `${practitioner.visibility_score}%`
            : '-'}
        </Text>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (practitioner: Practitioner) => {
        if (!practitioner.created_at) return <Text color="gray.400">-</Text>
        const date = new Date(practitioner.created_at)
        return <Text fontSize="sm">{date.toLocaleDateString()}</Text>
      },
    },
  ]

  return (
    <Container maxW="7xl">
      <VStack gap={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="end">
          <VStack align="start" gap={2}>
            <Heading size="2xl" color="gray.900">
              Practitioners
            </Heading>
            <Text fontSize="lg" color="gray.600">
              View and manage all practitioners
            </Text>
          </VStack>
          <Button
            leftIcon={<Icon as={FiRefreshCw} />}
            onClick={fetchPractitioners}
            variant="outline"
          >
            Refresh
          </Button>
        </HStack>

        {/* Filters */}
        <Card.Root>
          <Card.Body p={4}>
            <HStack gap={4} flexWrap="wrap">
              <HStack flex={1} minW="200px">
                <Icon as={FiSearch} color="gray.400" />
                <Input
                  placeholder="Search by ID, name, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </HStack>
              <Select
                value={stepFilter}
                onChange={(e) => {
                  setStepFilter(e.target.value)
                  setCurrentPage(1)
                }}
                placeholder="All Steps"
                w="150px"
              >
                <option value="0">Step 0</option>
                <option value="1">Step 1</option>
                <option value="2">Step 2</option>
                <option value="3">Step 3</option>
                <option value="4">Step 4</option>
                <option value="5">Step 5</option>
              </Select>
            </HStack>
          </Card.Body>
        </Card.Root>

        {/* Error Alert */}
        {error && (
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>
              {error}
            </Alert.Content>
          </Alert.Root>
        )}

        {/* Practitioners Table */}
        <Card.Root>
          <Card.Body p={0}>
            <DataTable
              data={filteredPractitioners}
              columns={columns}
              onRowClick={(practitioner) =>
                navigate(`/admin/practitioners/${practitioner.practitioner_id}`)
              }
              isLoading={isLoading}
              emptyMessage="No practitioners found"
            />
            {!isLoading && filteredPractitioners.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredPractitioners.length / pageSize)}
                onPageChange={setCurrentPage}
              />
            )}
          </Card.Body>
        </Card.Root>
      </VStack>
    </Container>
  )
}

